import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/form';
import { useViewer } from '@/features/session';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useCreateWorkspace } from '../hooks/use-create-workspace';
import { emailDomain } from '../model/email-domain';
import {
  WORKSPACE_NAME_DEFAULTS,
  type WorkspaceNameValues,
  workspaceNameSchema,
} from '../schemas/workspace-name-schema';

type WelcomeFormProps = { email: string; suggestedName: string };

export function WelcomeForm({ email, suggestedName }: WelcomeFormProps) {
  const { refreshViewer } = useViewer();
  const { createWorkspace } = useCreateWorkspace();
  const form = useForm<WorkspaceNameValues>({
    resolver: zodResolver(workspaceNameSchema),
    defaultValues: { ...WORKSPACE_NAME_DEFAULTS, name: suggestedName },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, register, setError, formState } = form;

  const onSubmit = async ({ name }: WorkspaceNameValues) => {
    const outcome = await createWorkspace(name);
    // With a workspace in the session, the route guard moves on to Get started by itself.
    if (outcome.isCreated || outcome.errorCode === 'WORKSPACE_EXISTS') {
      await refreshViewer();
      return;
    }
    setError('name', {
      type: 'server',
      message: 'Use letters, numbers and spaces, 2 to 60 characters.',
    });
  };

  const domain = emailDomain(email);
  return (
    <FormProvider {...form}>
      <form
        data-enter=""
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="flex flex-col gap-2.5"
      >
        <FormField name="name">
          <FormItem>
            <FormLabel>Workspace name</FormLabel>
            <FormControl hasDescription>
              <Input size="lg" type="text" autoComplete="organization" {...register('name')} />
            </FormControl>
            <FormMessage
              description={
                domain ? `Suggested from your email domain, ${domain}.` : 'You can rename it later.'
              }
            />
          </FormItem>
        </FormField>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          isBlock
          isLoading={formState.isSubmitting}
        >
          Continue
        </Button>
      </form>
    </FormProvider>
  );
}
